/**
 * User Activity Tracking Utility
 * Tracks user interaction events to determine if the user is actively using the application
 */

export class UserActivityTracker {
    private lastUserActivity: number = Date.now();
    private isUserActive: boolean = true;
    private inactivityThreshold: number;
    private checkInterval: number | null = null;
    private activityListeners: (() => void)[] = [];
    private activityChangeListeners: ((isActive: boolean) => void)[] = [];
    private debug: boolean;

    // Events that indicate user activity
    private readonly activityEvents = [
        'mousedown',
        'mousemove',
        'keypress',
        'scroll',
        'touchstart',
        'click',
        'keydown',
        'wheel'
    ];

    constructor(options: {
        inactivityThreshold?: number; // milliseconds
        checkInterval?: number; // milliseconds
        debug?: boolean;
    } = {}) {
        this.inactivityThreshold = options.inactivityThreshold ?? 1000 * 60 * 5; // 5 minutes default
        this.debug = options.debug ?? false;

        this.init();
        this.startPeriodicCheck(options.checkInterval ?? 1000 * 30); // 30 seconds default
    } private init() {
        // Check if we're in a browser environment
        if (typeof document === 'undefined') {
            if (this.debug) console.warn("[USER_ACTIVITY] UserActivityTracker: document is not available, skipping initialization");
            return;
        }

        const updateActivity = () => this.updateUserActivity();

        // Add event listeners for user activity
        this.activityEvents.forEach(event => {
            const listener = () => updateActivity();
            document.addEventListener(event, listener, { passive: true });
            this.activityListeners.push(() => {
                document.removeEventListener(event, listener);
            });
        });

        if (this.debug) console.log(`[USER_ACTIVITY] UserActivityTracker initialized with ${this.inactivityThreshold}ms threshold`);
    }

    private updateUserActivity() {
        const wasInactive = !this.isUserActive;
        this.lastUserActivity = Date.now();
        this.isUserActive = true;

        // Notify listeners if activity status changed
        if (wasInactive && this.debug) console.log("[USER_ACTIVITY] User became active again");
        if (wasInactive) this.notifyActivityChange(true);
    }

    private startPeriodicCheck(interval: number) {
        this.checkInterval = setInterval(() => {
            const timeSinceLastActivity = Date.now() - this.lastUserActivity;
            const wasActive = this.isUserActive;
            this.isUserActive = timeSinceLastActivity < this.inactivityThreshold;

            // Notify listeners if activity status changed
            if (wasActive && !this.isUserActive && this.debug) console.log("[USER_ACTIVITY] User became inactive");
            if (wasActive !== this.isUserActive) this.notifyActivityChange(this.isUserActive);
        }, interval) as unknown as number;
    }

    /** Notify all activity change listeners */
    private notifyActivityChange(isActive: boolean) {
        this.activityChangeListeners.forEach(listener => {
            try {
                listener(isActive);
            } catch (error) {
                if (this.debug) console.error("[USER_ACTIVITY] Error in activity change listener:", error);
            }
        });
    }

    /** Check if the user is currently active */
    public isActive(): boolean {
        return this.isUserActive;
    }

    /** Get the timestamp of the last user activity */
    public getLastActivityTime(): number {
        return this.lastUserActivity;
    }

    /** Get the time since last activity in milliseconds */
    public getTimeSinceLastActivity(): number {
        return Date.now() - this.lastUserActivity;
    }

    /** Check if both tab is visible and user is active */
    public isActiveAndVisible(): boolean {
        // Check if we're in a browser environment
        if (typeof document === 'undefined') return false;
        return this.isActive() && document.visibilityState === 'visible';
    }

    /** Clean up event listeners and intervals */
    public destroy() {
        // Remove all event listeners
        this.activityListeners.forEach(removeListener => removeListener());
        this.activityListeners = [];

        // Clear activity change listeners
        this.activityChangeListeners = [];

        // Clear the interval
        if (this.checkInterval) {
            clearInterval(this.checkInterval);
            this.checkInterval = null;
        }

        if (this.debug) console.log("[USER_ACTIVITY] UserActivityTracker destroyed");
    }

    /** Reset the activity timer (mark user as active now) */
    public resetActivity() {
        this.updateUserActivity();
    }

    /** Update the inactivity threshold */
    public setInactivityThreshold(threshold: number) {
        this.inactivityThreshold = threshold;
        if (this.debug) console.log(`[USER_ACTIVITY] Inactivity threshold updated to ${threshold}ms`);
    }

    /** Listener for when the user's activity status changes */
    public onActivityChange(callback: (isActive: boolean) => void): () => void {
        this.activityChangeListeners.push(callback);

        // Return a cleanup function
        return () => {
            const index = this.activityChangeListeners.indexOf(callback);
            if (index > -1) this.activityChangeListeners.splice(index, 1);
        };
    }
}

// Create a default instance for easy use
export const userActivity = new UserActivityTracker();

// Helper function for quick checks
export function isUserActiveAndVisible(): boolean {
    return userActivity.isActiveAndVisible();
}

export function isUserActive(): boolean {
    return userActivity.isActive();
}

// Helper function to listen for activity changes
export function onUserActivityChange(callback: (isActive: boolean) => void): () => void {
    return userActivity.onActivityChange(callback);
}